/**
 * Idempotency storage: persistenza idempotency keys in SQLite.
 */

import { db } from '../connection';
import type {
  IdempotencyRecord,
  IdempotencyCheckResult,
} from './types';
import { IDEMPOTENCY_KEY_TTL_HOURS } from './types';
import { hashPayload } from './key-generator';
import { debugLog } from '../../utils/debug';

interface IdempotencyRow {
  key: string;
  operationType: string;
  requestHash: string;
  localEntityId: string;
  serverEntityId: string | null;
  status: string;
  cachedResponse: string | null;
  responseStatus: number | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

function _rowToRecord(row: IdempotencyRow): IdempotencyRecord {
  return {
    key: row.key,
    operationType: row.operationType as IdempotencyRecord['operationType'],
    requestHash: row.requestHash,
    localEntityId: row.localEntityId,
    serverEntityId: row.serverEntityId,
    status: row.status as IdempotencyRecord['status'],
    cachedResponse: row.cachedResponse,
    responseStatus: row.responseStatus,
    error: row.error,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    expiresAt: row.expiresAt,
  };
}

export class IdempotencyStorage {
  async initialize(): Promise<void> {
    await db.execute(`
      CREATE TABLE IF NOT EXISTS idempotency_keys (
        key TEXT PRIMARY KEY,
        operation_type TEXT NOT NULL,
        request_hash TEXT NOT NULL,
        local_entity_id TEXT NOT NULL,
        server_entity_id TEXT,
        status TEXT NOT NULL DEFAULT 'pending',
        cached_response TEXT,
        response_status INTEGER,
        error TEXT,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        expires_at TEXT NOT NULL
      )
    `);
    await db.execute(`
      CREATE INDEX IF NOT EXISTS idx_idempotency_local_entity ON idempotency_keys(local_entity_id)
    `);
    await db.execute(`
      CREATE INDEX IF NOT EXISTS idx_idempotency_expires ON idempotency_keys(expires_at)
    `);
    await db.execute(`
      CREATE INDEX IF NOT EXISTS idx_idempotency_status ON idempotency_keys(status)
    `);
  }

  async check(key: string, payload: unknown): Promise<IdempotencyCheckResult> {
    const record = await this.get(key);
    if (!record) return { isDuplicate: false };

    if (new Date(record.expiresAt) < new Date()) {
      await this.delete(key);
      return { isDuplicate: false };
    }

    const currentHash = hashPayload(payload);
    if (currentHash !== record.requestHash) {
      return { isDuplicate: true, isConflict: true, existingRecord: record };
    }

    if (record.status === 'completed' && record.cachedResponse) {
      return {
        isDuplicate: true,
        cachedResponse: JSON.parse(record.cachedResponse) as unknown,
        cachedStatus: record.responseStatus ?? 200,
        existingRecord: record,
      };
    }

    return { isDuplicate: true, existingRecord: record };
  }

  async register(
    key: string,
    operationType: string,
    localEntityId: string,
    payload: unknown,
    ttlHours: number = IDEMPOTENCY_KEY_TTL_HOURS
  ): Promise<IdempotencyRecord> {
    const existing = await this.get(key);
    if (existing && existing.status === 'pending') {
      return existing;
    }

    const now = new Date();
    const expiresAt = new Date(now.getTime() + ttlHours * 60 * 60 * 1000);
    const requestHash = hashPayload(payload);

    const record: IdempotencyRecord = {
      key,
      operationType: operationType as IdempotencyRecord['operationType'],
      requestHash,
      localEntityId,
      serverEntityId: null,
      status: 'pending',
      cachedResponse: null,
      responseStatus: null,
      error: null,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    };

    await db.run(
      `INSERT INTO idempotency_keys
       (key, operation_type, request_hash, local_entity_id, server_entity_id,
        status, cached_response, response_status, error, created_at, updated_at, expires_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        record.key,
        record.operationType,
        record.requestHash,
        record.localEntityId,
        record.serverEntityId,
        record.status,
        record.cachedResponse,
        record.responseStatus,
        record.error,
        record.createdAt,
        record.updatedAt,
        record.expiresAt,
      ]
    );

    return record;
  }

  async markCompleted(
    key: string,
    serverEntityId: string,
    response: unknown,
    responseStatus: number = 201
  ): Promise<void> {
    await db.run(
      `UPDATE idempotency_keys
       SET status = 'completed', server_entity_id = ?, cached_response = ?, response_status = ?, updated_at = ?
       WHERE key = ?`,
      [serverEntityId, JSON.stringify(response), responseStatus, new Date().toISOString(), key]
    );
  }

  async markFailed(key: string, error: string): Promise<void> {
    await db.run(
      `UPDATE idempotency_keys SET status = 'failed', error = ?, updated_at = ? WHERE key = ?`,
      [error, new Date().toISOString(), key]
    );
  }

  async get(key: string): Promise<IdempotencyRecord | null> {
    const results = await db.query<IdempotencyRow>(
      `SELECT key, operation_type as operationType, request_hash as requestHash,
              local_entity_id as localEntityId, server_entity_id as serverEntityId,
              status, cached_response as cachedResponse, response_status as responseStatus,
              error, created_at as createdAt, updated_at as updatedAt, expires_at as expiresAt
       FROM idempotency_keys WHERE key = ?`,
      [key]
    );
    const row = results[0];
    return row ? _rowToRecord(row) : null;
  }

  async getByLocalEntityId(localEntityId: string): Promise<IdempotencyRecord[]> {
    const rows = await db.query<IdempotencyRow>(
      `SELECT key, operation_type as operationType, request_hash as requestHash,
              local_entity_id as localEntityId, server_entity_id as serverEntityId,
              status, cached_response as cachedResponse, response_status as responseStatus,
              error, created_at as createdAt, updated_at as updatedAt, expires_at as expiresAt
       FROM idempotency_keys WHERE local_entity_id = ? ORDER BY created_at DESC`,
      [localEntityId]
    );
    return rows.map(_rowToRecord);
  }

  async delete(key: string): Promise<void> {
    await db.run('DELETE FROM idempotency_keys WHERE key = ?', [key]);
  }

  async cleanup(): Promise<number> {
    const result = await db.run(
      'DELETE FROM idempotency_keys WHERE expires_at < ?',
      [new Date().toISOString()]
    );
    if (result.changes > 0) {
      debugLog(`[Idempotency] Cleaned up ${result.changes} expired records`);
    }
    return result.changes;
  }

  async getStats(): Promise<{
    total: number;
    pending: number;
    completed: number;
    failed: number;
    expired: number;
  }> {
    const now = new Date().toISOString();
    const results = await db.query<{ status: string; count: number; expired: number }>(
      `SELECT status, COUNT(*) as count,
              SUM(CASE WHEN expires_at < ? THEN 1 ELSE 0 END) as expired
       FROM idempotency_keys GROUP BY status`,
      [now]
    );

    const stats = { total: 0, pending: 0, completed: 0, failed: 0, expired: 0 };
    for (const row of results) {
      stats.total += row.count;
      stats.expired += row.expired;
      switch (row.status) {
        case 'pending': stats.pending = row.count; break;
        case 'completed': stats.completed = row.count; break;
        case 'failed': stats.failed = row.count; break;
      }
    }
    return stats;
  }
}

export const idempotencyStorage = new IdempotencyStorage();
